<template>
  <section class="article bg-black4 font-Spectral">
    <div class="lg:m-auto lg:w-11/12 lg:max-w-6xl">
      <div class="m-auto bg-beige1 px-4 pb-14 pt-12 md:px-10 md:pb-20 md:pt-7 lg:px-[10%] lg:py-32">
        <div class="m-auto max-w-[783px]">
          <div class="article__main-text">
            <template v-for="(content, index) in enStory.contents">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-if="content.fieldId === 'richEditor'" :key="index" v-html="content.richEditor"></div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-if="content.fieldId === 'html'" :key="index" v-html="content.html"></div>
              <div v-if="content.image" :key="index"><OptimizedImage alt="" :image-url="content.image.url" :optimized-image-options="{ width: 1280 }" /></div>
            </template>
          </div>
          <div v-if="enStory.provider.logoImg.url" class="mb-14 mt-8 flex flex-col items-center border-t border-solid border-gray1 pt-14 md:my-16 md:flex-row md:border md:px-10 md:pb-8 md:pt-6">
            <OptimizedImage :class="['mb-14 w-[136px] md:mb-0 md:mr-10 md:w-[130px]']" :image-url="enStory.provider.logoImg.url" :optimized-image-options="{ width: 280 }" />
            <div>
              <p class="mb-4 text-center text-base text-black2 md:text-left md:text-black1">{{ enStory.provider.name }}</p>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p class="article__provider-description mb-4 text-justify text-sm text-black2 md:text-[13px] md:leading-5 md:text-black1" v-html="enStory.provider.description"></p>
            </div>
          </div>
          <div v-if="featuredExperiences.length > 0">
            <h2 class="mb-6 text-center text-xl text-gold1 md:text-left md:text-[22px]">Featured Experiences</h2>
            <template v-for="(experience, index) in featuredExperiences" :key="experience.id">
              <div
                class="experience__card relative flex flex-col border-gray1 bg-white p-4 transition-all duration-1000 md:flex-row md:items-center md:border-b md:bg-beige1 md:px-0 md:py-6"
                :class="[{ 'md:border-t': index === 0 }, { 'mt-4 md:mt-0': index !== 0 }]"
              >
                <OrganismsPagesEnExperienceCard
                  orientation="horizontal"
                  theme="light"
                  :aria-label="`featured-experience-${index}`"
                  :data-testid="`featured-experience-${index}`"
                  :display-multiple-images="true"
                  :experience="experience"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { EnStoryViewModel } from '@/models/enStory';
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { OptimizedImage } from '@/wabunka-src/common/components';

interface Props {
  enStory: EnStoryViewModel;
  featuredExperiences: WabunkaExperienceViewModel[];
}

defineProps<Props>();
</script>

<style lang="scss" scoped>
.article {
  .sp-none {
    display: none;

    @apply md:block;
  }

  .pc-none {
    display: block;

    @apply md:hidden;
  }

  &__ {
    &main-text {
      :deep(h3) {
        font-size: 18px;
        font-weight: 300;
        line-height: 26px;
        letter-spacing: 0.5px;
        margin-bottom: 0px;

        @apply text-gold1 md:mt-10 md:font-Spectral md:text-[22px] md:font-normal;
      }

      :deep(p) {
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0;
        text-align: justify;
        margin-bottom: 24px;
        font-weight: 300;

        @apply text-black2 md:text-justify md:font-Spectral md:text-base md:tracking-wide;
      }

      :deep(.caption) {
        font-size: 12px;
        line-height: 14px;

        @apply text-gray2;
      }

      :deep(img) {
        width: 100%;
        margin-bottom: 8px;
      }

      :deep(hr) {
        border: none;
        border-bottom: 1px solid #c4c4c4;
        margin: 0 0 24px;

        @apply md:my-10 md:block;
      }

      .experience {
        &__ {
          :deep {
            &card {
              &:before {
                @apply md:absolute md:block md:bg-[#efe9e1] md:duration-500;
              }

              &:hover {
                &:before {
                  width: 100%;
                  transition: 0.5s;
                }

                img {
                  -webkit-transform: scale(1.1);
                  transform: scale(1.1);
                  transition: 0.5s;
                }
              }
            }
          }
        }
      }
    }

    &provider-description {
      :deep(strong) {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }
}
</style>
